import React, { useEffect, useState } from 'react'
import { X, Heart, DollarSign, Clock } from 'lucide-react'
import { formatCentsAsCurrency } from '../utils/currency'
import confetti from 'canvas-confetti'

interface DonationNotificationData {
  id: string
  donorName: string
  amount: number
  currency: string
  message?: string
  auctionTitle: string
  charityName: string
  createdAt: string
}

interface DonationNotificationPopupProps {
  notification: DonationNotificationData
  onClose: () => void
  autoCloseDelay?: number
}

export const DonationNotificationPopup: React.FC<DonationNotificationPopupProps> = ({
  notification,
  onClose,
  autoCloseDelay = 45000 // 45 seconds for donations (longer than bids)
}) => {
  const [timeLeft, setTimeLeft] = useState(Math.floor(autoCloseDelay / 1000))
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Trigger confetti when donation notification appears
    const triggerConfetti = () => {
      // Multiple bursts of confetti for extra celebration
      const colors = ['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6', '#EC4899']
      
      // First burst
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: colors,
        gravity: 0.8,
        drift: 0.1
      })
      
      // Second burst with hearts
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 60,
          origin: { y: 0.7 },
          colors: ['#EC4899', '#EF4444', '#F59E0B'],
          shapes: ['circle'],
          gravity: 0.6
        })
      }, 300)
      
      // Third burst - more spread out
      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 100,
          origin: { y: 0.5 },
          colors: colors,
          gravity: 0.9
        })
      }, 600)
    }

    // Show animation and trigger confetti
    const showTimer = setTimeout(() => {
      setIsVisible(true)
      triggerConfetti()
    }, 100)

    // Auto-close timer
    const autoCloseTimer = setTimeout(() => {
      handleClose()
    }, autoCloseDelay)

    // Countdown timer
    const countdownInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      clearTimeout(showTimer)
      clearTimeout(autoCloseTimer)
      clearInterval(countdownInterval)
    }
  }, [autoCloseDelay])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300) // Wait for animation to complete
  }

  return (
    <div
      className={`
        fixed top-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)]
        transform transition-all duration-300 ease-out
        ${isVisible 
          ? 'translate-x-0 opacity-100 scale-100' 
          : 'translate-x-full opacity-0 scale-95'
        }
      `}
    >
      <div className="relative bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 dark:from-pink-900/20 dark:via-purple-900/20 dark:to-indigo-900/20 rounded-xl shadow-2xl border-2 border-pink-200 dark:border-pink-700/50 overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/10 via-purple-400/10 to-indigo-400/10 animate-pulse"></div>
        
        {/* Sparkle effects */}
        <div className="absolute top-2 left-2 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
        <div className="absolute top-4 right-8 w-1.5 h-1.5 bg-pink-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-4 left-8 w-1 h-1 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 transition-colors z-10"
          aria-label="Close notification"
        >
          <X className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>

        {/* Content */}
        <div className="relative p-6 pr-12">
          {/* Header with special donation icon */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="p-2 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full">
                <Heart className="h-6 w-6 text-white" fill="currentColor" />
              </div>
              <div className="absolute -top-1 -right-1 p-1 bg-yellow-400 rounded-full">
                <DollarSign className="h-3 w-3 text-yellow-800" />
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 dark:from-pink-400 dark:to-purple-400">
                💝 Donation Received!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Someone just supported your cause!
              </p>
            </div>
          </div>

          {/* Donation details */}
          <div className="space-y-3">
            {/* Donor info */}
            <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 border border-pink-200/50 dark:border-pink-700/30">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-pink-700 dark:text-pink-300">{notification.donorName}</span> donated to
              </p>
              <p className="font-semibold text-gray-900 dark:text-white text-sm">
                {notification.charityName}
              </p>
            </div>

            {/* Amount - prominently displayed */}
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg p-4 border border-green-200 dark:border-green-700/50">
              <div className="text-center">
                <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                  Donation Amount
                </p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {formatCentsAsCurrency(notification.amount, notification.currency)}
                </p>
              </div>
            </div>

            {/* Message if provided */}
            {notification.message && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-700/50">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                  💬 Message from donor:
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200 italic">
                  "{notification.message}"
                </p>
              </div>
            )}

            {/* Timer and action */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Clock className="h-3 w-3" />
                <span>Auto-close in {timeLeft}s</span>
              </div>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Celebrate! 🎉
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DonationNotificationPopup
